import _ from 'lodash';

import { CAPACITY_MANAGER_STARTUP_DELAY_SECONDS } from 'actions/routing/capacity_manager';
import GatewayErrorInteractiveHandler from 'scripts/application/lib/gateway_error_interactive_handler';
import qconsole from 'scripts/lib/qconsole';
import { RoutingChannel } from 'models/agent_routing_preferences';
import { VersionOutdated } from 'scripts/application/lib/error';

export default class UpdateAgentRoutingPreferences {
  constructor(context) {
    this.context = context;
    this.errorHandler = new GatewayErrorInteractiveHandler(this.context);
  }

  run(channels) {
    let pending = this.store.getPending();
    if (!pending) {
      pending = this.store.get();
      pending.incrementVersion();
    }

    pending.updateChannels(channels);
    this.store.setPending(pending);

    // Postpone capacity-based routing for channels affected by the change:
    // - when a channel gets selected it helps to avoid a race between the capacity manager
    //   and the routing triggered by the preference change.
    // - when a channel gets unselected it prevents a counter-intuitive UX
    //   of getting work routed on that channel while the unselect is being handled and throttled.
    this.postponeRouting();

    // Debounce the update because an agent can click multiple preferences in a short amount of time
    this.debounceUpdate();
  }

  /**
   * Postpones capacity-based routing for channels with a pending preference change.
   */
  postponeRouting() {
    if (this.hasPendingChangeFor(RoutingChannel.MESSAGING)) {
      this.context.capacityManager.postponeMessagingRouting(CAPACITY_MANAGER_STARTUP_DELAY_SECONDS);
    }

    if (this.hasPendingChangeFor(RoutingChannel.VOICE)) {
      this.context.capacityManager.postponeCallRouting(CAPACITY_MANAGER_STARTUP_DELAY_SECONDS);
    }
  }

  hasPendingChangeFor(channel) {
    return _.get(this.store.getPending(), ['channels', channel]) !== _.get(this.store.get(), ['channels', channel]);
  }

  debounceUpdate() {
    clearTimeout(this.constructor.throttleTimeout);
    this.constructor.throttleTimeout = setTimeout(() => this.updateBackendFromPending(), DEBOUNCE_DELAY);
  }

  updateBackendFromPending() {
    let pending = this.store.getPending();
    // Abort update if the pending state was reset or got outdated during the throttle interval
    if (!pending || pending._version <= this.store.get()._version) {
      return;
    }

    this.context.gateways.agentRoutingPreferences
      .set(pending.toJs())
      .then(() => this.handleSetSuccess())
      .catch(err => this.handleSetError(err));
  }

  handleSetError(err) {
    this.store.resetPending();

    if (err instanceof VersionOutdated) {
      qconsole.log(
        "Ignored version outdated from setting routing preferences. Someone else won the race and we'll get their state via broadcast"
      );
      return;
    }

    this.errorHandler.handleCommonErrors(err);
  }

  handleSetSuccess() {
    let pending = this.store.getPending();
    if (!pending) {
      return;
    }

    if (pending._version <= this.store.get()._version) {
      this.store.resetPending();
      return;
    }

    this.store.commitPending();
  }

  get store() {
    return this.context.stores.agentRoutingPreferences;
  }
}

const DEBOUNCE_DELAY = 1500;



// WEBPACK FOOTER //
// ./src/scripts/application/actions/routing/update_agent_routing_preferences.js